import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@shared/components';
import { postingsLabel } from '../../utils/fundStatementLabels';
import type { StatementLine } from '../../utils/statementBlocks';
import { LABEL_SPAN_TO_NET, STATEMENT_GRID, STATEMENT_GRID_TRACKS } from '../DealerFinancials/FundStatementPeriod/PeriodStatementBody/statementGrid';
import { StatementLineFigures } from '../DealerFinancials/FundStatementPeriod/PeriodStatementBody/StatementLineFigures';
import { StatementLineRow } from '../DealerFinancials/FundStatementPeriod/PeriodStatementBody/StatementLineRow';
import { StatementLineSubRows } from '../DealerFinancials/FundStatementPeriod/PeriodStatementBody/StatementLineSubRows';
import { UnsplitBadge } from '../DealerFinancials/FundStatementPeriod/PeriodStatementBody/UnsplitBadge';

interface Props {
    line: StatementLine;
    split: boolean;
    open: boolean;
    onToggle: () => void;
    currency: string;
    children?: ReactNode;
}

const lineRegionId = (key: string): string => `fund-account-line-${key}`;

export function StatementLineDisclosure({ line, split, open, onToggle, currency, children }: Props) {
    if (line.postings === 0) return <StatementLineRow line={line} split={split} currency={currency} />;
    const regionId = lineRegionId(line.key);
    const labelId = `${regionId}-label`;
    return (
        <>
            <div className={`${STATEMENT_GRID} text-sm`}>
                <dt className={`flex flex-wrap items-center gap-2 ${split ? '' : LABEL_SPAN_TO_NET}`}>
                    <Button
                        type="button"
                        size="xs"
                        variant="neutral"
                        appearance="ghost"
                        ariaExpanded={open}
                        ariaControls={regionId}
                        onClick={onToggle}
                        startIcon={<ChevronRight aria-hidden="true" className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} />}
                    >
                        <span id={labelId}>{postingsLabel(line.label, line.postings)}</span>
                    </Button>
                    {line.unsplit.postings > 0 && <UnsplitBadge postings={line.unsplit.postings} />}
                </dt>
                <StatementLineFigures line={line} split={split} currency={currency} />
            </div>
            {split && <StatementLineSubRows line={line} currency={currency} />}
            <div className={open ? STATEMENT_GRID_TRACKS : 'hidden'}>
                <dt className="sr-only">Postings</dt>
                <dd className="col-span-5">
                    <div
                        id={regionId}
                        role="region"
                        aria-labelledby={labelId}
                        hidden={!open}
                        className="ml-4 mt-1 space-y-2 border-l border-mp-border pl-4"
                    >
                        {open && children}
                    </div>
                </dd>
            </div>
        </>
    );
}
